<template>
  <HrPageLayout
    title="Audit Logs"
    subtitle="Complete activity trail with device, IP, channel, and exact timestamps"
    icon="📋"
    :breadcrumb-items="[
      { label: 'Dashboard', route: '/dashboard' },
      { label: 'System Management' },
      { label: 'Audit Logs' }
    ]"
  >
    <template #header-actions>
      <button type="button" class="hr-btn hr-btn-primary" @click="loadLogs" :disabled="loading">
        <i class="bx bx-refresh" :class="{ 'bx-spin': loading }"></i> Refresh
      </button>
    </template>

    <div class="audit-logs-content">
      <div class="hr-chart-card hr-mb-4">
        <div class="hr-chart-header hr-flex-between">
          <h5 class="hr-chart-title"><i class="bx bx-list-ul"></i> Filters</h5>
        </div>
        <div class="hr-chart-body">
          <div class="hr-d-flex hr-flex-wrap hr-gap-2">
            <div class="hr-filter-group">
              <label class="hr-form-label">Action type</label>
              <select v-model="filters.actionType" @change="loadLogs" class="hr-select" style="min-width: 180px;">
                <option value="">All</option>
                <option value="LOGIN">LOGIN</option>
                <option value="LOGOUT">LOGOUT</option>
                <option value="FAILED_LOGIN">FAILED_LOGIN</option>
                <option value="CREATE_USER">CREATE_USER</option>
                <option value="UPDATE_USER">UPDATE_USER</option>
                <option value="RESET_PASSWORD">RESET_PASSWORD</option>
                <option value="PASSWORD_CHANGE">PASSWORD_CHANGE</option>
                <option value="DELETE_USER">DELETE_USER</option>
                <option value="SUSPEND_ACCOUNT">SUSPEND_ACCOUNT</option>
                <option value="ACTIVATE_ACCOUNT">ACTIVATE_ACCOUNT</option>
                <option value="APPROVE_USER">APPROVE_USER</option>
                <option value="REJECT_USER">REJECT_USER</option>
                <option value="UPDATE">UPDATE</option>
                <option value="INSERT">INSERT</option>
                <option value="DOCUMENT_UPLOAD">DOCUMENT_UPLOAD</option>
                <option value="DOCUMENT_DOWNLOAD">DOCUMENT_DOWNLOAD</option>
                <option value="AUDIT">AUDIT</option>
              </select>
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">Channel</label>
              <select v-model="filters.source" @change="loadLogs" class="hr-select" style="min-width: 140px;">
                <option value="">All</option>
                <option value="WEB">WEB</option>
                <option value="MOBILE">MOBILE</option>
                <option value="USSD">USSD</option>
                <option value="API">API</option>
                <option value="CRON">CRON</option>
              </select>
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">Username / ID</label>
              <input v-model="filters.username" type="text" class="hr-input" placeholder="Filter by user" style="width: 160px;" @keyup.enter="loadLogs">
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">From date</label>
              <input v-model="filters.fromDate" type="date" class="hr-input" @change="loadLogs">
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">To date</label>
              <input v-model="filters.toDate" type="date" class="hr-input" @change="loadLogs">
            </div>
            <button type="button" class="hr-btn hr-btn-primary" @click="loadLogs">Apply</button>
          </div>
        </div>
      </div>

      <div class="hr-chart-card">
        <div class="hr-chart-header hr-flex-between">
          <h5 class="hr-chart-title">Logs ({{ total }} total)</h5>
        </div>
        <div class="hr-chart-body">
          <div v-if="loading" class="hr-loading-wrap">
            <div class="hr-spinner"></div>
            <p class="hr-text-muted">Loading audit logs...</p>
          </div>
          <div v-else class="hr-table-wrapper audit-table-wrap">
            <table class="hr-table hr-table-hover">
              <thead>
                <tr>
                  <th>Date &amp; Time</th>
                  <th>User</th>
                  <th>Action</th>
                  <th>Channel</th>
                  <th>Device</th>
                  <th>IP Address</th>
                  <th>API / Method</th>
                  <th>Description</th>
                  <th>Table / Record</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(log, i) in logs" :key="log.id || log.logId || (log.actionDate + '-' + i)">
                  <td class="audit-datetime">{{ formatDate(log.actionDate) }}</td>
                  <td>
                    <div>{{ log.username || log.idNo || '—' }}</div>
                    <div v-if="log.idNo && log.username && log.idNo !== log.username" class="hr-small hr-text-muted">{{ log.idNo }}</div>
                  </td>
                  <td><span class="hr-badge hr-badge-info">{{ log.actionType || '—' }}</span></td>
                  <td><span class="hr-badge" :class="sourceBadgeClass(log.source)">{{ log.source || '—' }}</span></td>
                  <td class="audit-device" :title="deviceTitle(log)">{{ deviceSummary(log) }}</td>
                  <td class="hr-small">{{ log.ipAddress || '—' }}</td>
                  <td class="hr-small hr-text-muted">
                    <div>{{ log.apiFunction || '—' }}</div>
                    <div v-if="log.requestMethod">{{ log.requestMethod }}</div>
                  </td>
                  <td class="hr-text-muted hr-small audit-desc" :title="log.description">{{ truncate(log.description, 100) }}</td>
                  <td class="hr-small">{{ log.tableName ? log.tableName + (log.recordID ? ' #' + log.recordID : '') : '—' }}</td>
                </tr>
                <tr v-if="logs.length === 0">
                  <td colspan="9" class="hr-text-center hr-py-4 hr-text-muted">No audit logs found.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import { authUrl, execute } from "@/api";
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";

export default {
  name: "AuditLogs",
  components: { HrPageLayout },
  data() {
    return {
      loading: false,
      logs: [],
      total: 0,
      filters: {
        actionType: "",
        source: "",
        username: "",
        fromDate: "",
        toDate: ""
      }
    };
  },
  mounted() {
    this.loadLogs();
  },
  methods: {
    async loadLogs() {
      this.loading = true;
      const data = new FormData();
      data.append("function", "getAuditLogs");
      data.append("limit", "200");
      data.append("offset", "0");
      if (this.filters.actionType) data.append("actionType", this.filters.actionType);
      if (this.filters.source) data.append("source", this.filters.source);
      if (this.filters.username) data.append("username", this.filters.username.trim());
      if (this.filters.fromDate) data.append("fromDate", this.filters.fromDate);
      if (this.filters.toDate) data.append("toDate", this.filters.toDate);
      try {
        const res = await execute(data, authUrl);
        const d = res?.data?.data;
        this.logs = Array.isArray(d?.logs) ? d.logs : [];
        this.total = d?.total ?? 0;
      } catch (_) {
        this.logs = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },
    formatDate(val) {
      if (!val) return "—";
      try {
        return new Date(val).toLocaleString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false
        });
      } catch (_) {
        return val;
      }
    },
    truncate(str, len) {
      if (!str) return "—";
      return str.length <= len ? str : str.slice(0, len) + "…";
    },
    deviceSummary(log) {
      const d = (log.deviceDetails || "").trim();
      if (d) {
        const osMatch = d.match(/os:\s*([^|]+)/i);
        const browserMatch = d.match(/browser:\s*([^|]+)/i);
        if (osMatch || browserMatch) {
          return [browserMatch?.[1]?.trim(), osMatch?.[1]?.trim()].filter(Boolean).join(" · ");
        }
        return this.truncate(d, 48);
      }
      if (log.userAgent) return this.truncate(log.userAgent, 48);
      return "—";
    },
    deviceTitle(log) {
      const parts = [];
      if (log.deviceDetails) parts.push(log.deviceDetails);
      if (log.userAgent && log.userAgent !== log.deviceDetails) parts.push(log.userAgent);
      return parts.join("\n") || "";
    },
    sourceBadgeClass(source) {
      const s = String(source || "").toUpperCase();
      if (s === "MOBILE") return "hr-badge-warning";
      if (s === "USSD") return "hr-badge-success";
      if (s === "CRON") return "hr-badge-secondary";
      return "hr-badge-info";
    }
  }
};
</script>

<style scoped>
.audit-logs-content { padding: 0; }
.hr-small { font-size: 0.875rem; }
.audit-table-wrap { overflow-x: auto; }
.audit-datetime { white-space: nowrap; font-size: 0.875rem; }
.audit-device { max-width: 160px; font-size: 0.8125rem; }
.audit-desc { max-width: 280px; }
</style>
